import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, getStrings } from '../constants/strings';

interface LanguageContextType {
    language: Language;
    setLanguage: (language: Language) => void;
    strings: ReturnType<typeof getStrings>;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
    // Load saved language from localStorage or default to Estonian
    const [language, setLanguage] = useState<Language>(() => {
        const saved = localStorage.getItem('selectedLanguage');
        return (saved === 'en' || saved === 'et' || saved === 'ru') ? saved : 'et';
    });
    
    const strings = getStrings(language);

    // Persist language changes to localStorage
    useEffect(() => {
        localStorage.setItem('selectedLanguage', language);
    }, [language]);

    const value = {
        language,
        setLanguage,
        strings,
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}; 